import { z } from "zod";

import {
  cableConstructionSchema,
  cableSizingStatusSchema,
  conductorArrangementSchema,
  conductorMaterialSchema,
  installationMethodSchema,
  insulationMaterialSchema,
} from "./cableContract";

// The controlled vocabularies of the cable tray and routing check (EOS-12, Master Prompt A34),
// copied verbatim from the backend so a value the API can send is never rejected here. Sources
// at commit 8a2686c: backend/app/domain/electrical/cable_tray/models.py (TrayType,
// TrayArrangement, LayerMode, CoverKind, RatedLoadBasis, CableRowSource) and .../results.py
// (CableTrayStatus, CableTrayWarningCode and the three severity sets, FillBasis, LoadBasis); the
// allowance basis and the ALLOWANCE_NOT_APPLIED_TO_WIDTH code of the CTO addendum of 2026-10-18.
// cableTrayContract.test.ts pins each list.
//
// The cable's construction, arrangement, installation method, material, insulation and the run
// status are the cable module's enums: reused from that contract rather than written out again.

export const cableTrayStatusSchema = z.enum(["DESIGN_CHECK_PASSED", "REVIEW_REQUIRED", "NO_SOLUTION"]);

export const cableTrayWarningCodeSchema = z.enum([
  "FILL_LIMIT_EXCEEDED",
  "SINGLE_LAYER_WIDTH_EXCEEDED",
  "COVER_DEPTH_EXCEEDED",
  "LOAD_EXCEEDED",
  "RATED_LOAD_SPAN_NOT_APPLICABLE",
  "FILL_LIMIT_NOT_ESTABLISHED",
  "RATED_LOAD_NOT_DECLARED",
  "RATED_SPAN_NOT_DECLARED",
  "SUPPORT_SPACING_NOT_DECLARED",
  "DIMENSIONS_SOURCE_NOT_DECLARED",
  "FILL_LIMIT_SOURCE_NOT_DECLARED",
  "RATED_LOAD_SOURCE_NOT_DECLARED",
  "SUPPORT_SPACING_SOURCE_NOT_DECLARED",
  "OVERALL_DIAMETER_NOT_DECLARED",
  "MASS_NOT_DECLARED",
  "DIAMETER_SOURCE_NOT_DECLARED",
  "MASS_SOURCE_NOT_DECLARED",
  "CABLE_RUN_NOT_VALID",
  "INSTALLATION_METHOD_MISMATCH",
  "GROUPING_DIFFERS",
  "INSTALLATION_METHOD_TRAY_GENERIC",
  "INSTALLATION_METHOD_ENGINEERED",
  "SPACING_NOT_DECLARED",
  "QUANTITY_FROM_LINKED_RUN",
  "FUTURE_ALLOWANCE_NOT_DECLARED",
  "ALLOWANCE_NOT_APPLIED_TO_WIDTH",
  "CIRCUMSCRIBED_CIRCLE_BASIS",
  "SEGREGATION_NOT_CHECKED",
  "COVER_THERMAL_EFFECT_NOT_CHECKED",
  "EARTH_CONTINUITY_NOT_CHECKED",
  "BEND_RADIUS_NOT_CHECKED",
  "TRAY_MECHANICAL_DESIGN_NOT_CHECKED",
]);

export const trayTypeSchema = z.enum([
  "CABLE_TRAY_PERFORATED",
  "CABLE_TRAY_SOLID",
  "CABLE_LADDER",
  "WIRE_MESH",
]);
export const trayArrangementSchema = z.enum([
  "SINGLE_LAYER_TOUCHING",
  "SINGLE_LAYER_SPACED",
  "TREFOIL_TOUCHING",
  "TREFOIL_SPACED",
  "MULTI_LAYER",
]);
export const layerModeSchema = z.enum(["MULTI_LAYER", "SINGLE_LAYER"]);
export const coverKindSchema = z.enum(["NONE", "VENTILATED", "SOLID"]);
export const ratedLoadBasisSchema = z.enum(["PER_METRE", "PER_SPAN"]);
export const allowanceBasisSchema = z.enum(["AREA_AND_LOAD", "WIDTH_AND_LOAD"]);
export const cableRowSourceSchema = z.enum(["LINKED_CABLE_RUN", "DECLARED"]);
export const fillBasisSchema = z.enum([
  "DECLARED_LIMIT",
  "USABLE_WIDTH",
  "NOT_ESTABLISHED",
  "NOT_CHECKED",
]);
export const loadBasisSchema = z.enum(["DECLARED_RATING", "SPAN_NOT_APPLICABLE", "NOT_CHECKED"]);

export {
  cableConstructionSchema,
  cableSizingStatusSchema,
  conductorArrangementSchema,
  conductorMaterialSchema,
  installationMethodSchema,
  insulationMaterialSchema,
};

/** A computed failure only (CTO decision D4 of 2026-10-18): NO_SOLUTION. */
export const NO_SOLUTION_WARNING_CODES = [
  "FILL_LIMIT_EXCEEDED",
  "SINGLE_LAYER_WIDTH_EXCEEDED",
  "COVER_DEPTH_EXCEEDED",
  "LOAD_EXCEEDED",
] as const;

/** A condition that could not be evaluated, or a source the engineer still owes. */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "RATED_LOAD_SPAN_NOT_APPLICABLE",
  "FILL_LIMIT_NOT_ESTABLISHED",
  "RATED_LOAD_NOT_DECLARED",
  "RATED_SPAN_NOT_DECLARED",
  "SUPPORT_SPACING_NOT_DECLARED",
  "DIMENSIONS_SOURCE_NOT_DECLARED",
  "FILL_LIMIT_SOURCE_NOT_DECLARED",
  "RATED_LOAD_SOURCE_NOT_DECLARED",
  "SUPPORT_SPACING_SOURCE_NOT_DECLARED",
  "OVERALL_DIAMETER_NOT_DECLARED",
  "MASS_NOT_DECLARED",
  "DIAMETER_SOURCE_NOT_DECLARED",
  "MASS_SOURCE_NOT_DECLARED",
  "CABLE_RUN_NOT_VALID",
] as const;

/** What the sheet states; never moves the status. */
export const INFORMATION_WARNING_CODES = [
  "INSTALLATION_METHOD_MISMATCH",
  "GROUPING_DIFFERS",
  "INSTALLATION_METHOD_TRAY_GENERIC",
  "INSTALLATION_METHOD_ENGINEERED",
  "SPACING_NOT_DECLARED",
  "QUANTITY_FROM_LINKED_RUN",
  "FUTURE_ALLOWANCE_NOT_DECLARED",
  "ALLOWANCE_NOT_APPLIED_TO_WIDTH",
  "CIRCUMSCRIBED_CIRCLE_BASIS",
  "SEGREGATION_NOT_CHECKED",
  "COVER_THERMAL_EFFECT_NOT_CHECKED",
  "EARTH_CONTINUITY_NOT_CHECKED",
  "BEND_RADIUS_NOT_CHECKED",
  "TRAY_MECHANICAL_DESIGN_NOT_CHECKED",
] as const;

/** The codes a row may carry; every other code belongs to the route. */
export const ROW_WARNING_CODES = [
  "OVERALL_DIAMETER_NOT_DECLARED",
  "MASS_NOT_DECLARED",
  "DIAMETER_SOURCE_NOT_DECLARED",
  "MASS_SOURCE_NOT_DECLARED",
  "CABLE_RUN_NOT_VALID",
  "INSTALLATION_METHOD_MISMATCH",
  "GROUPING_DIFFERS",
  "INSTALLATION_METHOD_TRAY_GENERIC",
  "INSTALLATION_METHOD_ENGINEERED",
  "SPACING_NOT_DECLARED",
  "QUANTITY_FROM_LINKED_RUN",
] as const;

/** The five items every route states it did not check (decision D6; the F-1 lesson of IR-005). */
export const ALWAYS_ROUTE_INFORMATION_CODES = [
  "SEGREGATION_NOT_CHECKED",
  "COVER_THERMAL_EFFECT_NOT_CHECKED",
  "EARTH_CONTINUITY_NOT_CHECKED",
  "BEND_RADIUS_NOT_CHECKED",
  "TRAY_MECHANICAL_DESIGN_NOT_CHECKED",
] as const;

export type CableTrayStatus = z.infer<typeof cableTrayStatusSchema>;
export type CableTrayWarningCode = z.infer<typeof cableTrayWarningCodeSchema>;
export type TrayType = z.infer<typeof trayTypeSchema>;
export type TrayArrangement = z.infer<typeof trayArrangementSchema>;
export type LayerMode = z.infer<typeof layerModeSchema>;
export type CoverKind = z.infer<typeof coverKindSchema>;
export type RatedLoadBasis = z.infer<typeof ratedLoadBasisSchema>;
export type AllowanceBasis = z.infer<typeof allowanceBasisSchema>;
export type CableRowSource = z.infer<typeof cableRowSourceSchema>;
export type FillBasis = z.infer<typeof fillBasisSchema>;
export type LoadBasis = z.infer<typeof loadBasisSchema>;
